import React from "react";
import { Typography } from "@mui/material";

import ConfirmDialog from "../../shared/ConfirmDialog";

export interface DeleteTargetDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Name of the target being deleted, surfaced in the confirmation message. */
  targetName: string;
  /** Called when the dialog is dismissed (X icon, backdrop, or Cancel button). */
  onClose: () => void;
  /** Called when the user confirms the deletion. */
  onConfirm: () => void;
  /** Shows a spinner on the confirm button and disables all actions while the delete is in flight. */
  isLoading?: boolean;
}

/**
 * Destructive confirmation dialog for permanently deleting a target.
 *
 * @param props
 * @param props.open       - Whether the dialog is visible
 * @param props.targetName - Quoted in the confirmation message
 * @param props.onClose    - Neutral dismiss (X / backdrop / Cancel)
 * @param props.onConfirm  - User confirmed deletion — caller fires the mutation
 * @param [props.isLoading=false] - Disables actions while the mutation runs
 */
const DeleteTargetDialog: React.FC<DeleteTargetDialogProps> = ({
  open,
  targetName,
  onClose,
  onConfirm,
  isLoading = false,
}) => (
  <ConfirmDialog
    open={open}
    title="Delete Target?"
    confirmLabel="Delete"
    confirmColor="error"
    cancelLabel="Cancel"
    isLoading={isLoading}
    onClose={onClose}
    onCancel={onClose}
    onConfirm={onConfirm}
  >
    <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
      This will permanently delete the target &lsquo;{targetName}&rsquo; and all associated session
      data. This action cannot be undone.
    </Typography>
  </ConfirmDialog>
);

export default DeleteTargetDialog;
